
import React from "react";
import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

type StatusCardItem = {
  id: string;
  title: string;
  description: string;
};

type StatusCardProps = {
  title: string;
  status: "pending" | "processing" | "completed" | "error" | "warning";
  icon: React.ReactNode;
  description: string;
  items: StatusCardItem[];
  action?: {
    label: string;
    onClick: () => void;
    variant?: "default" | "destructive" | "outline" | "secondary";
    className?: string; // Making className optional on action
  };
};

const getStatusConfig = (status: StatusCardProps["status"]) => {
  switch (status) {
    case "pending":
      return {
        badgeVariant: "outline" as const,
        badgeLabel: "Beklemede",
        borderColor: "border-orange-300",
        iconColor: "text-orange-500",
      };
    case "processing":
      return {
        badgeVariant: "secondary" as const,
        badgeLabel: "İşleniyor",
        borderColor: "border-blue-300",
        iconColor: "text-blue-500",
      };
    case "completed":
      return {
        badgeVariant: "default" as const,
        badgeLabel: "Tamamlandı",
        borderColor: "border-green-300",
        iconColor: "text-green-500",
      };
    case "error":
      return {
        badgeVariant: "destructive" as const,
        badgeLabel: "Hata",
        borderColor: "border-red-300",
        iconColor: "text-red-500",
      };
    case "warning":
      return {
        badgeVariant: "outline" as const,
        badgeLabel: "Uyarı",
        borderColor: "border-yellow-300",
        iconColor: "text-yellow-500",
      };
  }
};

const StatusCard = ({
  title,
  status,
  icon,
  description,
  items,
  action,
}: StatusCardProps) => {
  const statusConfig = getStatusConfig(status);
  
  return (
    <Card className={cn("overflow-hidden", statusConfig.borderColor, "border-l-4")}>
      <CardContent className="p-4">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center gap-2">
            <div className={cn("p-1.5 rounded-md", statusConfig.iconColor)}>
              {icon}
            </div>
            <div>
              <h3 className="font-medium">{title}</h3>
              <p className="text-xs text-muted-foreground">{description}</p>
            </div>
          </div>
          <Badge variant={statusConfig.badgeVariant}>{statusConfig.badgeLabel}</Badge>
        </div>
        
        <div className="mt-3 space-y-2">
          {items.map((item) => (
            <div key={item.id} className="border-b pb-2 last:border-0 last:pb-0">
              <div className="font-medium text-sm">{item.title}</div>
              <div className="text-xs text-muted-foreground">{item.description}</div>
            </div>
          ))}
        </div>
      </CardContent>
      
      {action && (
        <CardFooter className="pt-0 pb-3 px-4">
          <Button 
            variant={action.variant || "outline"} 
            size="sm" 
            className={cn("w-full text-xs", action.className)} 
            onClick={action.onClick}
          >
            {action.label}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default StatusCard;
